import { BUSINESS_PLAN_NAME, PROFESSIONAL_PLAN_NAME } from '../subscription/planLockTexts';

/**
 * Rótulos e agrupamento das Novidades da Gabriela (docs/features/changelog/README.md).
 * Compartilhado pela tela logada (/system/novidades) e pela página pública (/novidades).
 */

export const CHANGELOG_TYPES = {
  feature: { label: 'Novidade', plural: 'Novidades', color: 'var(--color-primary)', bg: 'var(--color-blue-light4)' },
  improvement: { label: 'Melhoria', plural: 'Melhorias', color: '#2E7D32', bg: '#E8F5E9' },
  fix: { label: 'Correção', plural: 'Correções', color: '#B45309', bg: '#FFF4E5' },
};

export const CHANGELOG_AREAS = {
  agenda: 'Agenda',
  pacientes: 'Pacientes',
  atendimento: 'Atendimento',
  financeiro: 'Financeiro',
  fiscal: 'Notas fiscais',
  whatsapp: 'WhatsApp',
  marketing: 'Marketing',
  'equipe-e-conta': 'Equipe e conta',
  assinatura: 'Assinatura',
  'seguranca-e-privacidade': 'Segurança e privacidade',
  geral: 'Geral',
};

// Ordem dos planos: o selo some quando o plano atual já inclui a novidade
export const CHANGELOG_PLANS = {
  essencial: { name: 'Essencial', order: 1 },
  profissional: { name: PROFESSIONAL_PLAN_NAME, order: 2 },
  business: { name: BUSINESS_PLAN_NAME, order: 3 },
};

export const typeInfo = (type) => CHANGELOG_TYPES[type] || CHANGELOG_TYPES.feature;
export const areaLabel = (area) => CHANGELOG_AREAS[area] || null;
export const planInfo = (plan) => (plan ? CHANGELOG_PLANS[plan] || null : null);

/** "Disponível a partir do plano Profissional" | "Disponível no plano Business" */
export const planBadgeText = (plan) => {
  const info = planInfo(plan);
  if (!info) return null;
  return info.name === BUSINESS_PLAN_NAME
    ? `Disponível no plano ${info.name}`
    : `Disponível a partir do plano ${info.name}`;
};

/** Ordem do plano pelo nome que vem da assinatura ("Profissional", "Plano Business"...); null se desconhecido. */
export const planOrderFromName = (name) => {
  if (!name) return null;
  const normalized = String(name).toLowerCase();
  const match = Object.entries(CHANGELOG_PLANS).find(([key]) => normalized.includes(key));
  return match ? match[1].order : null;
};

/** URL do vídeo da entrada: aceita string ou { src, poster, captions }. */
export const videoOf = (entry) => {
  const video = entry?.video;
  if (!video) return null;
  if (typeof video === 'string') return { src: video, poster: entry.image || null, captions: null };
  if (!video.src) return null;
  return { src: video.src, poster: video.poster || entry.image || null, captions: video.captions || null };
};

// ─── Semanas (segunda a domingo) ─────────────────────────────────────────────

const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho',
  'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

// "2026-10-10" em horário local, sem o deslocamento de fuso do new Date("2026-10-10")
const parseDay = (value) => {
  const [year, month, day] = String(value || '').slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const mondayOf = (date) => {
  const monday = new Date(date);
  monday.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  return monday;
};

const pad = (n) => String(n).padStart(2, '0');
const dayKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Semana ISO (mesmo id dos resumos: 2026-W41)
const isoWeekId = (monday) => {
  const thursday = new Date(monday);
  thursday.setDate(monday.getDate() + 3);
  const firstThursday = new Date(thursday.getFullYear(), 0, 4);
  const week = 1 + Math.round(((thursday - mondayOf(firstThursday)) / 86400000 - 3) / 7);
  return `${thursday.getFullYear()}-W${pad(week)}`;
};

/** "Semana de 5 a 11 de outubro" (com o ano só quando não é o ano corrente) */
export const weekLabel = (monday) => {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const currentYear = new Date().getFullYear();
  const yearOf = (date) => (date.getFullYear() !== currentYear ? ` de ${date.getFullYear()}` : '');
  const sameYear = monday.getFullYear() === sunday.getFullYear();
  if (monday.getMonth() === sunday.getMonth()) {
    return `Semana de ${monday.getDate()} a ${sunday.getDate()} de ${MONTHS[sunday.getMonth()]}${yearOf(sunday)}`;
  }
  const start = `${monday.getDate()} de ${MONTHS[monday.getMonth()]}${sameYear ? '' : yearOf(monday)}`;
  return `Semana de ${start} a ${sunday.getDate()} de ${MONTHS[sunday.getMonth()]}${yearOf(sunday)}`;
};

/**
 * Agrupa as entradas por semana, mais recente primeiro.
 * @returns {Array<{ id: string, label: string, highlights: Array, entries: Array, fixes: Array }>}
 */
export const groupByWeek = (entries) => {
  const weeks = new Map();
  entries.forEach((entry) => {
    const day = parseDay(entry.date);
    if (!day) return;
    const monday = mondayOf(day);
    const key = dayKey(monday);
    if (!weeks.has(key)) {
      weeks.set(key, { key, id: isoWeekId(monday), label: weekLabel(monday), highlights: [], entries: [], fixes: [] });
    }
    const week = weeks.get(key);
    if (entry.highlight) week.highlights.push(entry);
    else if (entry.type === 'fix') week.fixes.push(entry);
    else week.entries.push(entry);
  });
  return Array.from(weeks.values()).sort((a, b) => (a.key < b.key ? 1 : -1));
};
